/* eslint-disable sort-imports */
/**
 * The labor standard form: what the planner is editing, the product it is
 * for, the upcoming job orders a change would touch, and the one submit that
 * validates, builds the payload and hands it to the data hook's save.
 */
import { useCallback, useMemo, useState } from "react";

import { AffectedMOsResponse, LaborStandard, laborStandardsApi,OperationType } from "../../../../services/labor-standards-api";
import { Product } from "../../../../services/products-api";
import { formatCrew } from "../../utils/standard-matching";
import {
	EMPTY_FORM,
	emptyPhaseRow,
	formToOperations,
	OperationFormRow,
	PhaseFormRow,
	StandardFormData,
	standardToForm,
	validateForm,
	WorkstationOptionFormRow,
} from "../utils/standard-helpers";
import { SnackbarSeverity, StandardSavePayload } from "./use-labor-standards";

export type OperationFieldValue = string | number | boolean | null | WorkstationOptionFormRow[];
export type PhaseFieldValue = string | number | boolean | null;

export interface UseStandardFormArgs {
	products: Product[];
	operationTypes: OperationType[];
	save: (payload: StandardSavePayload, editingId: number | null) => Promise<LaborStandard | null>;
	notify: (message: string, severity: SnackbarSeverity) => void;
}

export function useStandardForm({ products, operationTypes, save, notify }: UseStandardFormArgs) {
	const [showForm, setShowForm] = useState(false);
	const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
	const [editingStandard, setEditingStandard] = useState<LaborStandard | null>(null);
	const [affectedMOs, setAffectedMOs] = useState<AffectedMOsResponse | null>(null);
	const [loadingAffectedMOs, setLoadingAffectedMOs] = useState(false);
	const [saving, setSaving] = useState(false);
	const [formData, setFormData] = useState<StandardFormData>(EMPTY_FORM);

	const reset = useCallback(() => {
		setFormData(EMPTY_FORM);
		setEditingStandard(null);
		setSelectedProduct(null);
		setAffectedMOs(null);
	}, []);

	const openNew = useCallback(() => {
		reset();
		setShowForm(true);
	}, [reset]);

	const openEdit = useCallback(
		async (standard: LaborStandard) => {
			const product = products.find((p) => p.id === standard.productId);
			setSelectedProduct(product || null);
			setEditingStandard(standard);

			// Fetch affected MOs
			if (standard.id) {
				setLoadingAffectedMOs(true);
				setAffectedMOs(null);
				try {
					const affected = await laborStandardsApi.getAffectedMOs(standard.id);
					setAffectedMOs(affected);
				} catch (error) {
					console.error("Failed to fetch affected MOs:", error);
				} finally {
					setLoadingAffectedMOs(false);
				}
			}

			setFormData(standardToForm(standard, formatCrew));
			setShowForm(true);
		},
		[products]
	);
	const edit = useCallback((standard: LaborStandard) => void openEdit(standard), [openEdit]);

	const close = useCallback(() => {
		setShowForm(false);
		reset();
	}, [reset]);

	// ---- Field setters ----

	const setProductId = useCallback(
		(productId: number) => setSelectedProduct(products.find((p) => p.id === productId) || null),
		[products]
	);
	const setField = useCallback((field: "recipeGroup" | "batchSize" | "batchUnit" | "description", value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	}, []);

	// ---- Operations ----

	const addOperation = useCallback(() => {
		setFormData((prev) => ({
			...prev,
			operations: [
				...prev.operations,
				{
					name: "",
					operationTypeId: operationTypes[0]?.id.toString() || "",
					// Most recipes stay on one line; the previous step's work center is
					// the likeliest default and is one click to change.
					workstation: prev.operations[prev.operations.length - 1]?.workstation || "",
					workstationOptions: [],
					parallelGroup: null,
					notes: "",
					phases: [emptyPhaseRow()],
				},
			],
		}));
	}, [operationTypes]);

	const removeOperation = useCallback((opIndex: number) => {
		setFormData((prev) => ({ ...prev, operations: prev.operations.filter((_, i) => i !== opIndex) }));
	}, []);

	const changeOperation = useCallback((opIndex: number, field: keyof OperationFormRow, value: OperationFieldValue) => {
		setFormData((prev) => ({
			...prev,
			operations: prev.operations.map((op, i) => (i === opIndex ? { ...op, [field]: value } : op)),
		}));
	}, []);

	// ---- Phases ----

	const addPhase = useCallback((opIndex: number) => {
		setFormData((prev) => ({
			...prev,
			operations: prev.operations.map((op, i) => (i === opIndex ? { ...op, phases: [...op.phases, emptyPhaseRow()] } : op)),
		}));
	}, []);

	const removePhase = useCallback((opIndex: number, phaseIndex: number) => {
		setFormData((prev) => ({
			...prev,
			operations: prev.operations.map((op, i) =>
				i === opIndex ? { ...op, phases: op.phases.filter((_, j) => j !== phaseIndex) } : op
			),
		}));
	}, []);

	const changePhase = useCallback((opIndex: number, phaseIndex: number, field: keyof PhaseFormRow, value: PhaseFieldValue) => {
		setFormData((prev) => ({
			...prev,
			operations: prev.operations.map((op, i) =>
				i === opIndex
					? { ...op, phases: op.phases.map((phase, j) => (j === phaseIndex ? { ...phase, [field]: value } : phase)) }
					: op
			),
		}));
	}, []);

	// Group ids already in play, so the picker can offer them on any operation
	// regardless of position — that is the whole point of groups over adjacency.
	const usedParallelGroups = useMemo(
		() =>
			[...new Set(formData.operations.map((op) => op.parallelGroup).filter((group): group is number => group != null))].sort(
				(a, b) => a - b
			),
		[formData.operations]
	);

	// ---- Submit ----

	const submit = useCallback(async () => {
		const problem = validateForm(formData, Boolean(selectedProduct));
		if (problem || !selectedProduct) {
			notify(problem ?? "No product selected", "error");
			return;
		}

		setSaving(true);
		try {
			const operations = formToOperations(formData);
			const payload: StandardSavePayload = {
				productId: selectedProduct.id,
				sku: selectedProduct.itemNumber,
				// The header work center is only the default: the server sets it to
				// the first operation's. Sent for older servers that still require it.
				workstation: operations[0]?.workstation || null,
				recipeGroup: formData.recipeGroup.trim() || null,
				batchSize: parseInt(formData.batchSize),
				batchUnit: formData.batchUnit.trim() || null,
				totalDurationMinutes: 0, // Will be calculated by backend
				description: formData.description || undefined,
				isActive: true,
				operations,
			};
			const saved = await save(payload, editingStandard?.id ?? null);
			if (saved) {
				setShowForm(false);
				reset();
			}
		} finally {
			setSaving(false);
		}
	}, [formData, selectedProduct, editingStandard, save, notify, reset]);
	const handleSubmit = useCallback(() => void submit(), [submit]);

	const canSubmit =
		!saving && Boolean(formData.batchSize) && formData.operations.length > 0 && formData.operations.every((op) => Boolean(op.workstation));

	return {
		showForm,
		selectedProduct,
		editingStandard,
		affectedMOs,
		loadingAffectedMOs,
		saving,
		formData,
		usedParallelGroups,
		canSubmit,
		openNew,
		edit,
		close,
		setProductId,
		setField,
		addOperation,
		removeOperation,
		changeOperation,
		addPhase,
		removePhase,
		changePhase,
		handleSubmit,
	};
}

export type StandardForm = ReturnType<typeof useStandardForm>;
